import React, { Fragment, PureComponent, ReactNode } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Tooltip from '@atlaskit/tooltip';

import { withApdex } from 'src/components/apdex/with-apdex';
import { Repository } from 'src/components/types';
import { ConnectFileModuleMenu } from 'src/connect/file-modules';
import { ApdexTask } from 'src/types/apdex';

import { RepositoryPrivilege } from '../../../types';
import LfsTag from '../containers/lfs-tag';
import { FileEditorMode, FileMeta } from '../types';
import { isBinary, isImage, isLFS } from '../utils/file';

import EditableFilePath from './editable-file-path';
import FileActionsMenu from './file-actions-menu';
import { FileContents } from './file-contents';
import FilePath from './file-path';
import FileWrapper from './file-wrapper';
import messages from './file.i18n';
import * as styles from './file.style';

type Props = {
  buildPathSegmentUrl: (path: string) => string;
  connectEditorModuleKey?: string;
  connectViewerModuleKey?: string;
  contents?: string;
  fileEditorMode?: FileEditorMode;
  hash: string;
  isAtHead?: boolean;
  isLoading?: boolean;
  meta?: FileMeta;
  onChangeFileEditorMode: (fileEditorMode: FileEditorMode) => void;
  onCommitChanges?: (path: string, contents: string) => void;
  onDelete?: () => void;
  onOpenRaw?: () => void;
  onRename?: () => void;
  onSelectConnectEditor: (editor?: string) => void;
  onSelectConnectViewer: (viewer?: string) => void;
  onToggleAnnotate?: () => void;
  path: string;
  repository: Repository;
  repositoryAccessLevel?: RepositoryPrivilege;
  userIsAnonymous: boolean;
};

type InjectedProps = {
  intl: InjectedIntl;
};

type State = {
  newPath: string;
  prevIsEditing: boolean;
};

class SourceFile extends PureComponent<Props & InjectedProps, State> {
  static defaultProps: Partial<Props> = {
    fileEditorMode: 'read-only',
    onChangeFileEditorMode: () => {},
    onCommitChanges: () => {},
  };

  state = {
    newPath: this.props.path,
    prevIsEditing: this.props.fileEditorMode !== 'read-only',
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    const isEditing = props.fileEditorMode !== 'read-only';
    if (isEditing !== state.prevIsEditing) {
      return {
        // Reset the path to the original when enabling or disabling the editor
        newPath: props.path,
        prevIsEditing: isEditing,
      };
    }
    return null;
  }

  editingDisabledReason = () => {
    const { intl, isAtHead, meta, userIsAnonymous } = this.props;

    if (!meta) {
      return null;
    }

    if (isBinary(meta) || isImage(meta)) {
      return intl.formatMessage(messages.editDisabledReasonNonText);
    }

    if (isLFS(meta)) {
      return intl.formatMessage(messages.editDisabledReasonLFS);
    }

    if (!isAtHead) {
      return intl.formatMessage(messages.editDisabledReasonNonHead);
    }

    if (userIsAnonymous) {
      return intl.formatMessage(messages.editDisabledReasonAnonymousUser);
    }
    return null;
  };

  handleCommitChanges = (contents: string) => {
    if (this.props.onCommitChanges) {
      this.props.onCommitChanges(this.state.newPath, contents);
    }
  };

  handleEditorEnable = () => {
    if (this.props.onChangeFileEditorMode) {
      this.props.onChangeFileEditorMode('editor');
    }
  };

  handlePathChange = (path: string) => {
    this.setState({ newPath: path });
  };

  renderButtons = () => {
    if (this.props.fileEditorMode !== 'read-only') {
      return (
        <ConnectFileModuleMenu
          defaultLabel={this.props.intl.formatMessage(
            messages.fileEditorMenuLabel
          )}
          moduleKey={this.props.connectEditorModuleKey}
          moduleType="fileEditors"
          onSelect={this.props.onSelectConnectEditor}
        />
      );
    }

    const editingDisabledReason = this.editingDisabledReason();
    const canEdit = this.props.meta && editingDisabledReason === null;

    const editButton = (
      <CustomThemeButton
        appearance="subtle"
        isDisabled={!canEdit}
        onClick={this.handleEditorEnable}
      >
        <FormattedMessage {...messages.editAction} />
      </CustomThemeButton>
    );

    return (
      <styles.MenuContainer>
        <ConnectFileModuleMenu
          defaultLabel={this.props.intl.formatMessage(
            messages.fileViewerMenuLabel
          )}
          moduleKey={this.props.connectViewerModuleKey}
          moduleType="fileViews"
          onSelect={this.props.onSelectConnectViewer}
        />
        {canEdit ? (
          editButton
        ) : (
          <Tooltip position="left" content={editingDisabledReason}>
            {editButton}
          </Tooltip>
        )}
      </styles.MenuContainer>
    );
  };

  renderPath = (path: string, copyButton: ReactNode) => {
    const { meta, repository } = this.props;
    const { newPath } = this.state;
    const [, slug] = repository.full_name.split('/');
    const tags = meta && isLFS(meta) ? <LfsTag /> : null;

    if (this.props.fileEditorMode !== 'read-only') {
      return (
        <EditableFilePath
          onChange={this.handlePathChange}
          path={newPath}
          repositorySlug={slug}
        />
      );
    }

    return (
      <Fragment>
        {tags && <styles.Tags>{tags}</styles.Tags>}
        <FilePath
          buildPathSegmentUrl={this.props.buildPathSegmentUrl}
          path={path}
          repositorySlug={slug}
        />
        <styles.CopyButtonWrapper>{copyButton}</styles.CopyButtonWrapper>
      </Fragment>
    );
  };

  render() {
    const { fileEditorMode, path, repository } = this.props;

    const fileActionsMenu = (
      <FileActionsMenu
        hash={this.props.hash}
        isAtHead={this.props.isAtHead}
        onDelete={this.props.onDelete}
        onOpenRaw={this.props.onOpenRaw}
        onRename={this.props.onRename}
        onToggleAnnotate={this.props.onToggleAnnotate}
        path={path}
        repositoryAccessLevel={this.props.repositoryAccessLevel}
        repositoryFullSlug={repository.full_name}
        userIsAnonymous={this.props.userIsAnonymous}
      />
    );

    return (
      <FileWrapper
        buttons={this.renderButtons()}
        dropdownMenu={
          fileEditorMode === 'read-only' ? fileActionsMenu : undefined
        }
        filePath={path}
        renderFilePath={this.renderPath}
      >
        <FileContents
          connectEditorModuleKey={this.props.connectEditorModuleKey}
          connectViewerModuleKey={this.props.connectViewerModuleKey}
          contents={this.props.contents}
          // FIXME: default props should have fixed this...
          // @ts-ignore
          fileEditorMode={fileEditorMode}
          hash={this.props.hash}
          isLoading={this.props.isLoading}
          meta={this.props.meta}
          onChangeFileEditorMode={this.props.onChangeFileEditorMode}
          onCommitChanges={this.handleCommitChanges}
          onSelectConnectEditor={this.props.onSelectConnectEditor}
          path={path}
          repository={repository}
        />
      </FileWrapper>
    );
  }
}

export { SourceFile as BareSourceFile };
export default withApdex(ApdexTask.FileBrowse)(injectIntl(SourceFile));
